import { lazy, useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import axios from 'axios'

import * as routes from '../routes'
import { Layout, DateRangePicker, AlertContext } from '../widgets'
import {
  Loading,
  useAdminTitle,
} from '../shared'

const AdminCharts = lazy(() => import('./components/AdminCharts'))

const AdminAnalytics = (props) => {
  const [state, replaceState] = useState({
    loading: true,
    preset: 'last90',
    startDate: dayjs().utc().subtract(90, 'days'),
    endDate: dayjs().utc().subtract(1, 'days'),
    interval: 'day',
    rawCharts: [],
    charts: [],
  })

  const setState = (subState) => {
    replaceState(state => {
      return {...state, ...subState}
    })
  }

  useEffect(() => {
    loadData(state.startDate, state.endDate)
  }, [])

  useEffect(() => {
    const el = document.querySelector('.content')
    if (el) {
      el.style.maxWidth = 'none'
      return () => { el.style.maxWidth = '' }
    }
  }, [])

  useAdminTitle('Analytics')

  const { handleError } = useContext(AlertContext)

  const aggregateByInterval = (dataPoints, interval) => {
    if (interval === 'day') return dataPoints

    const getGroupKey = (dayStr) => {
      const date = dayjs(dayStr, 'M/D/YY')
      if (interval === 'week') return date.startOf('week').format('M/D/YY')
      return date.format('MMM YYYY')
    }

    const groups = {}
    const order = []

    for (const point of dataPoints) {
      const key = getGroupKey(point.day)
      if (!groups[key]) {
        groups[key] = { day: key }
        order.push(key)
      }
      for (const [k, v] of Object.entries(point)) {
        if (k !== 'day') groups[key][k] = (groups[key][k] || 0) + v
      }
    }

    return order.map(key => groups[key])
  }

  const toLocalChartData = (serverData, startDate, endDate, interval) => {
    const localData = []
    for (const chartData of serverData) {
      const dataPointsByDay = chartData.dataPoints.reduce((out, dataPoint) => {
        var day = dataPoint.day
        out[day] = out[day] || []
        out[day].push(dataPoint)
        return out
      }, {})
      const dataPointNames = [...new Set(chartData.dataPoints.map(dataPoint => dataPoint.name))]
      const dataPoints = []

      for (var date = startDate; date.diff(endDate.endOf('day')) <= 0; date = date.add(1, 'days')) {
        const day = date.format('M/D/YY')
        const dataPointsOnDay = dataPointsByDay[day] || []
        for (const dataPointName of dataPointNames) {
          if (!dataPointsOnDay.find((dataPoint) => dataPoint.name === dataPointName)) {
            dataPointsOnDay.push({name: dataPointName, day: day, count: 0})
          }
        }

        dataPoints.push(dataPointsOnDay.reduce((out, dataPoint) => {
          out[dataPoint.name] = dataPoint.count
          return out
        }, {day: day}))
      }

      localData.push({
        name: chartData.name,
        type: chartData.type,
        dataPoints: aggregateByInterval(dataPoints, interval),
        dataPointNames: dataPointNames,
      })
    }

    return localData
  }

  const loadData = (startDate, endDate) => {
    const interval = state.interval
    axios.get(routes.adminAnalyticsUrl, {
      params: {
        start: startDate.format('YYYY-MM-DD'),
        end: endDate.format('YYYY-MM-DD'),
      }
    })
      .then((response) => {
        const rawCharts = response.data
        const charts = toLocalChartData(rawCharts, startDate, endDate, interval)
        setState({ rawCharts, charts })
      }).catch((error) => {
        handleError(error)
      }).finally(() => {
        setState({ loading: false })
      })
  }

  const onDateChanged = (stateKey, date) => {
    setState({
      items: [],
      offset: 0,
      [stateKey]: dayjs(date, 'YYYY-MM-DD').utc(),
    })
  }

  const onPresetChanged = (preset) => {
    if (preset === 'custom') {
      setState({ preset })
      return
    }
    const endDate = dayjs().utc().subtract(1, 'days')
    const startDate = {
      last30: () => dayjs().utc().subtract(30, 'days'),
      last90: () => dayjs().utc().subtract(90, 'days'),
      last12months: () => dayjs().utc().subtract(1, 'year'),
    }[preset]()
    setState({ preset, startDate, endDate })
    loadData(startDate, endDate)
  }

  const onIntervalChanged = (interval) => {
    const charts = toLocalChartData(state.rawCharts, state.startDate, state.endDate, interval)
    setState({ interval, charts })
  }

  if (state.loading) {
    return <Loading />
  }

  const body =
    <Layout>
      <Layout.Header>Analytics</Layout.Header>
      <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px'}}>
        <select value={state.interval} onChange={e => onIntervalChanged(e.target.value)}>
          <option value='day'>Day</option>
          <option value='week'>Week</option>
          <option value='month'>Month</option>
        </select>
        <select value={state.preset} onChange={e => onPresetChanged(e.target.value)}>
          <option value='last30'>Last 30 days</option>
          <option value='last90'>Last 90 days</option>
          <option value='last12months'>Last 12 months</option>
          <option value='custom'>Custom</option>
        </select>
        {state.preset === 'custom' &&
          <DateRangePicker
            minDate={'2018-10-06'}
            startDate={state.startDate}
            endDate={state.endDate}
            onDateChanged={onDateChanged}
            onGo={() => loadData(state.startDate, state.endDate)}
          />
        }
      </div>
      <AdminCharts charts={state.charts} />
    </Layout>

  return (
    <div className='body-container'>
      {body}
    </div>
  )
}

export default AdminAnalytics
